/**
 * Hebrew → Latin romanization in three systems, for pointed (with niqqud) and unpointed text.
 *
 * Sources, each read in full:
 *
 * - SBL academic and SBL general-purpose — *The SBL Handbook of Style*, 2nd ed. (2014),
 *   §5.1: the consonant tables (§5.1.1.1, §5.1.2.1), the vowel tables (§5.1.1.2, §5.1.2.2) and
 *   the notes (§5.1.1.4, §5.1.2.3). The academic style is reversible: matres lectionis become
 *   a circumflex (â ê î ô û), quiescent aleph is always ʾ, dagesh forte doubles the consonant,
 *   spirantization is not marked (note 4), a vocal shewa is ǝ and a silent one is not written
 *   (note 6), and a maqqef is a hyphen (note 9). The general-purpose style is phonetic; its
 *   spirant forms are optional and "any given decision should remain consistent" (note 3),
 *   and ts and sh are never doubled (note 4).
 * - ALA-LC — the Library of Congress "Hebrew and Yiddish" romanization table (loc.gov/catdir/
 *   cpso/romanization/hebrew.pdf). Its vowels come from the *sound* of the word, so the table
 *   itself says dictionaries are needed to supply them; here they are read from the points.
 *   The table does not mention doubling, so a dagesh forte is not written.
 *
 * Limits a program cannot get round: a qamats is ā unless it is a qamats qatan (Unicode has a
 * separate sign, U+05C7, for that); a hiriq or qubuts is written short unless a mater follows;
 * whether a shewa is vocal is worked out from the vowel before it, which is the textbook rule
 * and has exceptions; and an unpointed word has no vowels to read.
 */

export type HebrewSystem = "sbl-academic" | "sbl-general" | "ala-lc";
export type SpirantMode = "plain" | "modern" | "classical";

export interface HebrewOptions {
  /** General-purpose style: how to write letters without a dagesh (v, kh, f…). */
  spirants: SpirantMode;
  /** General-purpose style: write aleph and ayin as ’ and ‘ (otherwise omit them). */
  guttural: boolean;
  capitalize: boolean;
}

export interface HebrewResult {
  text: string;
  /** Hebrew-script words of two or more letters with no vowel points. */
  unpointed: number;
  words: number;
}

type VowelName =
  | ""
  | "patah"
  | "qamats"
  | "qamats-qatan"
  | "segol"
  | "tsere"
  | "hiriq"
  | "holam"
  | "qubuts"
  | "hataf-patah"
  | "hataf-segol"
  | "hataf-qamats";

interface Cluster {
  /** The base letter, final forms folded into the regular one. */
  ch: string;
  final: boolean;
  dagesh: boolean;
  shin: "" | "shin" | "sin";
  vowel: VowelName;
  sheva: boolean;
}

/** What a cluster is once its neighbours are read. */
type Kind =
  | "cons"
  | "absorbed"
  | "shureq"
  | "holam-mater"
  | "mater-hiriq"
  | "mater-tsere"
  | "mater-segol"
  | "mater-qubuts"
  | "mater-he";

const VOWEL_MARKS: Record<string, VowelName> = {
  "ֱ": "hataf-segol",
  "ֲ": "hataf-patah",
  "ֳ": "hataf-qamats",
  "ִ": "hiriq",
  "ֵ": "tsere",
  "ֶ": "segol",
  "ַ": "patah",
  "ָ": "qamats",
  "ֹ": "holam",
  "ֺ": "holam",
  "ֻ": "qubuts",
  "ׇ": "qamats-qatan",
};

const FINAL_FORMS: Record<string, string> = {
  ך: "כ",
  ם: "מ",
  ן: "נ",
  ף: "פ",
  ץ: "צ",
};

const BEGADKEFAT = new Set([..."בגדכפת"]);
const LONG_VOWELS = new Set<VowelName>(["qamats", "tsere", "holam"]);
const SHORT_VOWELS = new Set<VowelName>([
  "patah",
  "segol",
  "hiriq",
  "qubuts",
  "qamats-qatan",
]);

const PRIME = "ʹ";
const HEBREW_RUN = /[֑-ׇא-תיִ-ﭏ]+/g;
const PRESENTATION = /[יִ-ﭏ]/g;
const WORD_SPLIT = /(\s+)/;
const FIRST_LETTER = /\p{L}/u;
const POINT = /[ְ-ׇֻ]/;

/** Consonants: [SBL academic, SBL general-purpose, ALA-LC]. Letters with a rule of their own are handled in code. */
const LETTERS: Record<string, readonly [string, string, string]> = {
  א: ["ʾ", "’", "ʼ"],
  ב: ["b", "b", "v"],
  ג: ["g", "g", "g"],
  ד: ["d", "d", "d"],
  ה: ["h", "h", "h"],
  ו: ["w", "w", "ṿ"],
  ז: ["z", "z", "z"],
  ח: ["ḥ", "kh", "ḥ"],
  ט: ["ṭ", "t", "ṭ"],
  י: ["y", "y", "y"],
  כ: ["k", "k", "kh"],
  ל: ["l", "l", "l"],
  מ: ["m", "m", "m"],
  נ: ["n", "n", "n"],
  ס: ["s", "s", "s"],
  ע: ["ʿ", "‘", "ʻ"],
  פ: ["p", "p", "f"],
  צ: ["ṣ", "ts", "ts"],
  ק: ["q", "q", "ḳ"],
  ר: ["r", "r", "r"],
  ש: ["š", "sh", "sh"],
  ת: ["t", "t", "t"],
};

const SYSTEM_INDEX: Record<HebrewSystem, number> = {
  "sbl-academic": 0,
  "sbl-general": 1,
  "ala-lc": 2,
};

/** Spirant (no dagesh) forms of the begadkefat letters for the general-purpose style. */
const SPIRANT_FORMS: Record<SpirantMode, Record<string, string>> = {
  plain: {},
  modern: { ב: "v", כ: "kh", פ: "f" },
  classical: { ב: "v", ג: "gh", ד: "dh", כ: "kh", פ: "ph", ת: "th" },
};

const VOWEL_TEXT: Record<HebrewSystem, Record<VowelName, string>> = {
  "sbl-academic": {
    "": "",
    patah: "a",
    qamats: "ā",
    "qamats-qatan": "o",
    segol: "e",
    tsere: "ē",
    hiriq: "i",
    holam: "ō",
    qubuts: "u",
    "hataf-patah": "ă",
    "hataf-segol": "ĕ",
    "hataf-qamats": "ŏ",
  },
  "sbl-general": {
    "": "",
    patah: "a",
    qamats: "a",
    "qamats-qatan": "o",
    segol: "e",
    tsere: "e",
    hiriq: "i",
    holam: "o",
    qubuts: "u",
    "hataf-patah": "a",
    "hataf-segol": "e",
    "hataf-qamats": "o",
  },
  "ala-lc": {
    "": "",
    patah: "a",
    qamats: "a",
    "qamats-qatan": "o",
    segol: "e",
    tsere: "e",
    hiriq: "i",
    holam: "o",
    qubuts: "u",
    "hataf-patah": "a",
    "hataf-segol": "e",
    "hataf-qamats": "o",
  },
};

/** Vowel + mater combinations: [academic, general-purpose, ALA-LC]. */
const MATER_TEXT: Record<string, readonly [string, string, string]> = {
  "mater-hiriq": ["î", "i", "i"],
  "mater-tsere": ["ê", "e", "e"],
  "mater-segol": ["ê", "e", "e"],
  "mater-he": ["â", "ah", "ah"],
  "mater-qubuts": ["ū", "u", "u"],
  "holam-mater": ["ô", "o", "o"],
  shureq: ["û", "u", "u"],
};

function emptyCluster(ch: string): Cluster {
  const folded = FINAL_FORMS[ch];
  return {
    ch: folded ?? ch,
    final: folded !== undefined,
    dagesh: false,
    shin: "",
    vowel: "",
    sheva: false,
  };
}

function applyPoint(cluster: Cluster, ch: string): void {
  const vowel = VOWEL_MARKS[ch];
  if (vowel) {
    cluster.vowel = cluster.vowel || vowel;
  } else if (ch === "ְ") {
    cluster.sheva = true;
  } else if (ch === "ּ") {
    cluster.dagesh = true;
  } else if (ch === "ׁ") {
    cluster.shin = "shin";
  } else if (ch === "ׂ") {
    cluster.shin = "sin";
  }
}

function isLetter(code: number): boolean {
  return code >= 0x5_d0 && code <= 0x5_ea;
}

function parseWord(word: string): Cluster[] {
  const clusters: Cluster[] = [];
  const text = word
    .replace(PRESENTATION, (c) => c.normalize("NFKC"))
    .normalize("NFD");
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    if (isLetter(code)) {
      clusters.push(emptyCluster(ch));
    } else {
      const last = clusters.at(-1);
      if (last) {
        applyPoint(last, ch);
      }
    }
  }
  return clusters;
}

function bare(c: Cluster): boolean {
  return !(c.vowel || c.sheva || c.dagesh);
}

function markVav(c: Cluster, prev: Cluster, kinds: Kind[], i: number): void {
  if (bare(c) && prev.vowel === "holam" && !prev.sheva) {
    kinds[i] = "absorbed";
    kinds[i - 1] = "holam-mater";
  } else if (bare(c) && prev.vowel === "qubuts") {
    kinds[i] = "absorbed";
    kinds[i - 1] = "mater-qubuts";
  } else if (
    c.dagesh &&
    !c.vowel &&
    !c.sheva &&
    !prev.vowel &&
    !prev.sheva &&
    prev.ch !== "ו"
  ) {
    kinds[i] = "shureq";
  }
}

function markYodOrHe(
  cs: readonly Cluster[],
  prev: Cluster,
  kinds: Kind[],
  i: number
): void {
  const c = cs[i] as Cluster;
  if (!bare(c)) {
    return;
  }
  let mater: Kind | undefined;
  if (c.ch === "י") {
    mater = MATER_BY_VOWEL[prev.vowel];
  } else if (c.ch === "ה" && i === cs.length - 1 && prev.vowel === "qamats") {
    mater = "mater-he";
  }
  if (mater) {
    kinds[i] = "absorbed";
    kinds[i - 1] = mater;
  }
}

/** Decide, for each letter, whether it is a consonant or the mater of the vowel before it. */
function classify(cs: readonly Cluster[]): Kind[] {
  const kinds: Kind[] = cs.map(() => "cons");
  for (let i = 1; i < cs.length; i += 1) {
    const c = cs[i] as Cluster;
    const prev = cs[i - 1] as Cluster;
    if (kinds[i - 1] !== "cons") {
      continue;
    }
    if (c.ch === "ו") {
      markVav(c, prev, kinds, i);
    } else {
      markYodOrHe(cs, prev, kinds, i);
    }
  }
  for (let i = 0; i < cs.length; i += 1) {
    const c = cs[i] as Cluster;
    if (c.ch === "ו" && c.vowel === "holam" && kinds[i] === "cons") {
      kinds[i] = "holam-mater";
    }
  }
  return kinds;
}

const MATER_BY_VOWEL: Partial<Record<VowelName, Kind>> = {
  hiriq: "mater-hiriq",
  tsere: "mater-tsere",
  segol: "mater-segol",
};

function endsInLong(kind: Kind, c: Cluster): boolean {
  return kind !== "cons" || LONG_VOWELS.has(c.vowel);
}

/** True when the shewa under cluster `i` is pronounced (vocal) rather than silent. */
function shevaVocal(
  cs: readonly Cluster[],
  kinds: readonly Kind[],
  i: number,
  silent: readonly boolean[]
): boolean {
  const _c = cs[i] as Cluster;
  const prev = cs[i - 1];
  if (!prev || i === 0) {
    return true;
  }
  if (i === cs.length - 1) {
    return false;
  }
  if (silent[i - 1] && prev.sheva) {
    return true;
  }
  if (isForte(cs, kinds, i)) {
    return true;
  }
  const prevKind = kinds[i - 1] as Kind;
  if (endsInLong(prevKind, prev)) {
    return true;
  }
  return !SHORT_VOWELS.has(prev.vowel);
}

function shevaMap(cs: readonly Cluster[], kinds: readonly Kind[]): boolean[] {
  const silent: boolean[] = cs.map(() => false);
  for (let i = 0; i < cs.length; i += 1) {
    if ((cs[i] as Cluster).sheva) {
      silent[i] = !shevaVocal(cs, kinds, i, silent);
    }
  }
  return silent;
}

interface Ctx {
  system: HebrewSystem;
  options: HebrewOptions;
  pointed: boolean;
}

function idx(ctx: Ctx): number {
  return SYSTEM_INDEX[ctx.system];
}

/** A dagesh in a begadkefat letter is a plain stop (lene) unless a vowel comes before it. */
function isForte(
  cs: readonly Cluster[],
  kinds: readonly Kind[],
  i: number
): boolean {
  const c = cs[i] as Cluster;
  const prev = cs[i - 1];
  if (!c.dagesh || i === 0 || !prev || "אהחער".includes(c.ch)) {
    return false;
  }
  if (!BEGADKEFAT.has(c.ch)) {
    return true;
  }
  const prevKind = kinds[i - 1] as Kind;
  return prevKind !== "cons" || (!!prev.vowel && !prev.sheva);
}

function consonantText(c: Cluster, ctx: Ctx): string {
  if (c.ch === "ש") {
    if (c.shin === "sin") {
      return ["ś", "s", "ś"][idx(ctx)] as string;
    }
    return LETTERS.ש?.[idx(ctx)] as string;
  }
  const entry = LETTERS[c.ch];
  if (!entry) {
    return c.ch;
  }
  const text = entry[idx(ctx)] as string;
  if (c.ch === "ע" && ctx.system === "sbl-general" && !ctx.options.guttural) {
    return "";
  }
  if (c.ch === "א" && ctx.system === "sbl-general" && !ctx.options.guttural) {
    return "";
  }
  return text;
}

/** The letter, with the stop/spirant distinction the system and options call for. */
function stopOrSpirant(c: Cluster, ctx: Ctx): string {
  const base = consonantText(c, ctx);
  if (!BEGADKEFAT.has(c.ch)) {
    return base;
  }
  const spirant = ctx.pointed && !c.dagesh;
  if (ctx.system === "ala-lc") {
    // The LC table gives ב כ פ their spirant values v, kh, f and the dagesh forms b, k, p.
    return spirant ? base : (STOPS[c.ch] ?? base);
  }
  if (ctx.system === "sbl-general" && spirant) {
    return SPIRANT_FORMS[ctx.options.spirants][c.ch] ?? base;
  }
  return base;
}

const STOPS: Record<string, string> = { ב: "b", כ: "k", פ: "p" };

function vavText(ctx: Ctx): string {
  if (ctx.system === "sbl-general") {
    return ctx.options.spirants === "modern" ? "v" : "w";
  }
  return LETTERS.ו?.[idx(ctx)] as string;
}

function letterText(c: Cluster, ctx: Ctx): string {
  if (c.ch === "ו") {
    return vavText(ctx);
  }
  return stopOrSpirant(c, ctx);
}

function doubled(text: string, c: Cluster, ctx: Ctx): string {
  if (ctx.system === "ala-lc") {
    return text;
  }
  if (ctx.system === "sbl-general" && (c.ch === "צ" || c.ch === "ש")) {
    return text;
  }
  return text + text;
}

function vowelText(c: Cluster, ctx: Ctx, vocal: boolean): string {
  if (c.sheva) {
    if (!vocal) {
      return "";
    }
    return ctx.system === "sbl-academic" ? "ǝ" : "e";
  }
  return VOWEL_TEXT[ctx.system][c.vowel];
}

function needsPrime(out: string, next: string, ctx: Ctx): boolean {
  if (ctx.system === "sbl-academic" || !out || !next) {
    return false;
  }
  const last = out.at(-1) ?? "";
  const first = next[0] ?? "";
  return (
    ("skptdgz".includes(last) && first === "h") ||
    (last === "t" && first === "s")
  );
}

function append(out: string, piece: string, ctx: Ctx): string {
  return needsPrime(out, piece, ctx) ? `${out}${PRIME}${piece}` : out + piece;
}

/** A furtive patah sits under a final ח, ע or ה (mappiq) after a long vowel and is spoken before it. */
function isFurtive(
  cs: readonly Cluster[],
  kinds: readonly Kind[],
  i: number
): boolean {
  const c = cs[i] as Cluster;
  const prev = cs[i - 1];
  if (i !== cs.length - 1 || c.vowel !== "patah" || !prev) {
    return false;
  }
  if (!("חע".includes(c.ch) || (c.ch === "ה" && c.dagesh))) {
    return false;
  }
  return endsInLong(kinds[i - 1] as Kind, prev);
}

function emitCluster(
  cs: readonly Cluster[],
  kinds: readonly Kind[],
  silent: readonly boolean[],
  i: number,
  ctx: Ctx
): string {
  const c = cs[i] as Cluster;
  const kind = kinds[i] as Kind;
  if (kind === "absorbed") {
    return "";
  }
  let consonant = letterText(c, ctx);
  if (isForte(cs, kinds, i)) {
    consonant = doubled(consonant, c, ctx);
  }
  const mater = MATER_TEXT[kind];
  if (mater) {
    const ownLetter =
      kind === "shureq" ||
      (kind === "holam-mater" && c.ch === "ו" && c.vowel === "holam");
    return (ownLetter ? "" : consonant) + (mater[idx(ctx)] as string);
  }
  const vowel = vowelText(c, ctx, !silent[i]);
  if (isFurtive(cs, kinds, i)) {
    return vowel + consonant;
  }
  // ALA-LC: a yod with no vowel after a patah is the second half of "ai".
  if (
    ctx.system === "ala-lc" &&
    c.ch === "י" &&
    !vowel &&
    cs[i - 1]?.vowel === "patah"
  ) {
    return "i";
  }
  return consonant + vowel;
}

function capitalizeWord(text: string): string {
  const match = FIRST_LETTER.exec(text);
  if (!match) {
    return text;
  }
  const at = match.index;
  return (
    text.slice(0, at) + (text[at] ?? "").toUpperCase() + text.slice(at + 1)
  );
}

function romanizeWord(word: string, ctx: Ctx): string {
  const cs = parseWord(word);
  const kinds = classify(cs);
  const silent = shevaMap(cs, kinds);
  let out = "";
  for (let i = 0; i < cs.length; i += 1) {
    out = append(out, emitCluster(cs, kinds, silent, i, ctx), ctx);
  }
  return ctx.options.capitalize ? capitalizeWord(out) : out;
}

/** Hebrew punctuation: maqqef joins words, sof pasuq ends a verse. */
const PUNCTUATION: Record<string, string> = {
  "־": "-",
  "׃": ".",
  "׳": "’",
  "״": "”",
};
const PUNCT = /[־׃׳״]/g;

/** Romanize Hebrew-script text; anything that is not Hebrew passes through. */
export function romanizeHebrew(
  input: string,
  system: HebrewSystem,
  options: HebrewOptions
): HebrewResult {
  let unpointed = 0;
  let words = 0;
  const pieces = input
    .replace(PUNCT, (c) => PUNCTUATION[c] ?? c)
    .split(WORD_SPLIT);
  const text = pieces
    .map((piece) =>
      piece.replace(HEBREW_RUN, (word) => {
        const pointed = POINT.test(word.normalize("NFD"));
        words += 1;
        if (!pointed && parseWord(word).length >= 2) {
          unpointed += 1;
        }
        return romanizeWord(word, { system, options, pointed });
      })
    )
    .join("");
  return { text: text.normalize("NFC"), unpointed, words };
}

export const HEBREW_SYSTEMS: readonly HebrewSystem[] = [
  "sbl-academic",
  "sbl-general",
  "ala-lc",
];

export interface HebrewLetterRow {
  hebrew: string;
  name: string;
  values: readonly [string, string, string];
}

const TABLE_ORDER = "אבגדהוזחטיכלמנסעפצקרשת";
const LETTER_NAMES: Record<string, string> = {
  א: "aleph",
  ב: "bet",
  ג: "gimel",
  ד: "dalet",
  ה: "he",
  ו: "vav",
  ז: "zayin",
  ח: "khet",
  ט: "tet",
  י: "yod",
  כ: "kaph",
  ל: "lamed",
  מ: "mem",
  נ: "nun",
  ס: "samek",
  ע: "ayin",
  פ: "pe",
  צ: "tsade",
  ק: "qoph",
  ר: "resh",
  ש: "shin",
  ת: "tav",
};

/** The consonant table. Shin and sin are listed separately; spirant variants are described in the page. */
export function hebrewLetterRows(): HebrewLetterRow[] {
  const rows: HebrewLetterRow[] = [];
  for (const letter of TABLE_ORDER) {
    const entry = LETTERS[letter];
    if (!entry) {
      continue;
    }
    rows.push({
      hebrew: letter,
      name: LETTER_NAMES[letter] ?? "",
      values: entry,
    });
    if (letter === "ש") {
      rows.push({ hebrew: "שׂ", name: "sin", values: ["ś", "s", "ś"] });
    }
  }
  return rows;
}
